import type { Address } from "viem"
import type { Config } from "wagmi"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { IChunkedRpcStrategy } from "@/domains/library/rpc-strategy"

import {
  discoverUserNfpmPositions,
  type DiscoverUserNfpmPositionsResult,
} from "../lens/discover-user-nfpm-positions"
import { nfpmUserStats, type NfpmUserStatsReturnType } from "../lens/nfpm-user-stats"

export type PositionOnChainStatsInput = {
  tokenId: bigint
  liquidity: bigint
}

export type PositionOnChainStatsEntry = {
  tokenId: bigint
  result: NfpmUserStatsReturnType
}

/**
 * Chunked reader for `nfpmUserStats` across many position NFTs. Each position
 * requires two `simulateContract` calls (`collect` + `decreaseLiquidity`), so
 * grouping them via the `IChunkedRpcStrategy` keeps the RPC pressure bounded
 * and routes latency/error metrics through the `IOrchestratedQuerySender`.
 *
 * Positions with zero liquidity are skipped — the simulated `decreaseLiquidity`
 * would revert and none of the derived values would change from zero. The
 * callers keep those positions observable so UI can still render "Burned /
 * empty" states.
 */
export class ManualClPositionOnChainTransport {
  constructor(
    private readonly config: Config,
    private readonly chunkedRpcStrategy: IChunkedRpcStrategy,
  ) {}

  async loadPositionStats(
    positions: ReadonlyArray<PositionOnChainStatsInput>,
    params: {
      account: Address
      chainId: number
      nfpManagerAddress: Address
    },
    query: IOrchestratedQuerySender<unknown>,
    defaultChunkSize: number = 10,
  ): Promise<PositionOnChainStatsEntry[]> {
    const runnable = positions.filter((p) => p.liquidity > 0n)
    if (runnable.length === 0) return []

    const chunkResults = await this.chunkedRpcStrategy.executeChunked(
      [...runnable],
      query,
      defaultChunkSize,
      async (chunk: Array<PositionOnChainStatsInput>) => {
        const entries = await Promise.all(
          chunk.map(async (pos) => ({
            tokenId: pos.tokenId,
            result: await nfpmUserStats({
              account: params.account,
              chainId: params.chainId,
              config: this.config,
              tokenId: pos.tokenId,
              liquidity: pos.liquidity,
              nfpManagerAddress: params.nfpManagerAddress,
            }),
          })),
        )
        return entries
      },
      query.getRpcMetricsCollector(),
    )

    return chunkResults.flat()
  }

  /**
   * Enumerate every NFPM NFT belonging to `account`. Used to resolve real
   * NFP tokenIds until the indexer surfaces them (Proposal 4). Discovery lens
   * executes reads sequentially to avoid provider-side multicall packing.
   */
  async discoverNfpmPositions(params: {
    account: Address
    chainId: number
    nfpManagerAddress: Address
  }): Promise<DiscoverUserNfpmPositionsResult> {
    return discoverUserNfpmPositions({
      account: params.account,
      nfpManagerAddress: params.nfpManagerAddress,
      chainId: params.chainId,
      config: this.config,
    })
  }
}
